/**
 * Keypad romaji สำหรับมือถือ
 *
 * ทำไมไม่ใช้ keyboard ระบบ: กินครึ่งจอ + autocorrect + ช้ากว่าคอม 3 เท่า
 * romaji ญี่ปุ่นมีโครงตายตัว → พยัญชนะ + สระ = แตะ 2 ที ต่อ 1 ตัว
 *
 * กด k → ปุ่มสระสว่าง (armed) → กด a → ส่ง "ka"
 * กดสระเปล่าๆ ได้เลยสำหรับแถว あ
 */

const CONSONANTS = ['k', 's', 't', 'n', 'h', 'm', 'y', 'r', 'w', 'g', 'z', 'd', 'b', 'p'] as const
const VOWELS = ['a', 'i', 'u', 'e', 'o'] as const

/** พยัญชนะควบสำหรับ yōon — กด y ซ้ำหลังพยัญชนะได้ เช่น k → y → a = kya */
const SMALL_Y = 'y'

export interface Keypad {
  el: HTMLElement
  /** ล้างสถานะ armed ตอนขึ้นข้อใหม่ */
  reset(): void
  setVisible(on: boolean): void
}

export function createKeypad(onInput: (text: string) => void, onBackspace: () => void): Keypad {
  const el = document.createElement('div')
  el.className = 'keypad'

  /** พยัญชนะที่กดค้างไว้รอสระ — '' = ยังไม่ได้กด */
  let armed = ''

  const consonantBtns = new Map<string, HTMLButtonElement>()
  const vowelBtns: HTMLButtonElement[] = []

  function refresh(): void {
    for (const [c, btn] of consonantBtns) {
      btn.classList.toggle('is-armed', armed.startsWith(c) && armed.length > 0)
    }
    // ตอนยังไม่กดพยัญชนะ สระก็กดได้ (แถว あ) — ไม่หรี่
    for (const b of vowelBtns) b.classList.remove('is-dim')
  }

  function press(c: string): void {
    if (c === SMALL_Y && armed !== '' && armed !== SMALL_Y) {
      // k + y = kya ...
      armed += SMALL_Y
    } else {
      armed = armed === c ? '' : c
    }
    refresh()
  }

  function pressVowel(v: string): void {
    onInput(armed + v)
    armed = ''
    refresh()
  }

  const rowC = document.createElement('div')
  rowC.className = 'keypad-row consonants'
  for (const c of CONSONANTS) {
    const b = document.createElement('button')
    b.type = 'button'
    b.className = 'key'
    b.textContent = c
    b.addEventListener('click', () => press(c))
    consonantBtns.set(c, b)
    rowC.append(b)
  }

  const rowV = document.createElement('div')
  rowV.className = 'keypad-row vowels'
  for (const v of VOWELS) {
    const b = document.createElement('button')
    b.type = 'button'
    b.className = 'key'
    b.textContent = v
    b.addEventListener('click', () => pressVowel(v))
    vowelBtns.push(b)
    rowV.append(b)
  }

  const rowX = document.createElement('div')
  rowX.className = 'keypad-row extras'

  const mkExtra = (label: string, fn: () => void, wide = false): HTMLButtonElement => {
    const b = document.createElement('button')
    b.type = 'button'
    b.className = wide ? 'key key-wide' : 'key'
    b.textContent = label
    b.addEventListener('click', fn)
    return b
  }

  rowX.append(
    mkExtra('n', () => {
      onInput('n')
      armed = ''
      refresh()
    }),
    mkExtra('shi', () => {
      onInput('shi')
      armed = ''
      refresh()
    }),
    mkExtra('tsu', () => {
      onInput('tsu')
      armed = ''
      refresh()
    }),
    mkExtra('⌫', () => {
      if (armed) {
        armed = ''
        refresh()
      } else {
        onBackspace()
      }
    }),
  )

  el.append(rowC, rowV, rowX)

  return {
    el,
    reset() {
      armed = ''
      refresh()
    },
    setVisible(on: boolean) {
      el.classList.toggle('is-on', on)
    },
  }
}

const COARSE = '(pointer: coarse)'

/**
 * เดาว่าควรเปิด keypad ไหม
 *
 * ดูชนิดตัวชี้อย่างเดียว ไม่ดูความกว้าง — desktop ย่อหน้าต่างแคบก็ยังมี keyboard จริง
 * ซึ่งเร็วกว่า keypad เสมอ · ผู้ใช้สั่งทับได้ใน settings
 */
export function shouldUseKeypad(): boolean {
  return window.matchMedia(COARSE).matches
}

/** ต่อ tablet เข้า/ถอดออก หรือสลับโหมดสัมผัส — ต้องคิดใหม่ ไม่ใช่ตัดสินครั้งเดียวตอนโหลด */
export function onPointerTypeChange(cb: () => void): void {
  window.matchMedia(COARSE).addEventListener('change', cb)
}
